<template>
  <section ref="statsRef" class="stats-section">
    <div class="section-bg-blur"></div>

    <div class="stats-grid">
      <div class="stat-item" v-for="(item, index) in statsData" :key="item.label"
        :style="{ '--delay': index * 0.1 + 's' }">
        <div class="stat-card">
          <div class="card-corner"></div>

          <div class="stat-value">
            <span class="number">{{ item.current }}</span>
            <span class="unit">{{ item.unit }}</span>
          </div>

          <div class="stat-label-wrapper">
            <span class="label-dot"></span>
            <span class="stat-label">{{ item.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { reactive, ref, onMounted, onUnmounted } from 'vue'

const statsRef = ref(null)
const statsData = reactive([
  { label: '活跃成员', current: 0, target: 120, unit: 'K+' },
  { label: '技术讨论', current: 0, target: 450, unit: '场' },
  { label: '科技交流', current: 0, target: 89, unit: '篇' },
  { label: '日均互动', current: 0, target: 15, unit: 'M+' }
])

let animationId = null
const startAnimation = () => {
  const duration = 2000
  const startTime = performance.now()

  const update = (now) => {
    const elapsed = now - startTime
    const progress = Math.min(elapsed / duration, 1)

    // EaseOutExpo 缓动函数
    const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress)

    statsData.forEach(item => {
      item.current = Math.floor(easeProgress * item.target)
    })

    if (progress < 1) {
      animationId = requestAnimationFrame(update)
    }
  }
  animationId = requestAnimationFrame(update)
}

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      startAnimation()
    } else {
      statsData.forEach(item => item.current = 0)
    }
  }, { threshold: 0.3 })

  if (statsRef.value) observer.observe(statsRef.value)
})

onUnmounted(() => {
  if (animationId) cancelAnimationFrame(animationId)
})
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@700&family=Inter:wght@400;800&display=swap');

.stats-section {
  position: relative;
  padding: 120px 8%;
  background: #050505;
  /* 深色底色提升高级感 */
  overflow: hidden;
}

.section-bg-blur {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40%;
  height: 40%;
  background: radial-gradient(circle, rgba(64, 158, 255, 0.08) 0%, transparent 70%);
  transform: translate(-50%, -50%);
  filter: blur(80px);
  pointer-events: none;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  /* 细线分隔感 */
  background: rgba(255, 255, 255, 0.05);
  /* 网格线颜色 */
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.stat-item {
  background: #0a0a0a;
  padding: 60px 20px;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.02);

    .card-corner {
      opacity: 1;
    }
  }
}

.stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.card-corner {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 6px;
  height: 6px;
  border-top: 1px solid #409eff;
  border-right: 1px solid #409eff;
  opacity: 0.3;
  transition: 0.3s;
}

.stat-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 3.5rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
  display: flex;
  align-items: baseline;
  margin-bottom: 16px;

  .number {
    letter-spacing: -2px;
  }

  .unit {
    font-size: 0.9rem;
    color: #409eff;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    margin-left: 6px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
}

.stat-label-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-dot {
  width: 4px;
  height: 4px;
  background: #409eff;
  border-radius: 50%;
  box-shadow: 0 0 8px #409eff;
}

.stat-label {
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 500;
}

/* 响应式优化 */
@media (max-width: 1200px) {
  .stat-value {
    font-size: 2.8rem;
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    border-radius: 16px;
  }

  .stat-item {
    padding: 40px 10px;
  }

  .stat-value {
    font-size: 2.2rem;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>